import { useEffect } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { isLocalMode } from '@/services/localRuntimeClient';
import {
  CREATE_WORKFLOW_FROM_PROMPT,
  fetchWorkflowAssistantFeatures,
  isFeatureEnabled,
} from '@/services/workflowAssistantFeatures';

/**
 * Loads the project's assistant `features` into the app store. Mount once in
 * AppLayout so the capability is available app-wide. Persisted + refreshed per
 * session; project switches clear the stale value in `hydrateRouteContext`.
 * Local mode has no `features` endpoint, so it's treated as enabled.
 */
export function useAssistantFeatures(): void {
  const { projectName } = useCurrentProject();
  const { isAuthenticated, getOttoToken } = useAuth();
  const setAssistantFeatures = useAppStore((s) => s.setAssistantFeatures);

  useEffect(() => {
    if (isLocalMode()) {
      setAssistantFeatures([{ featureName: CREATE_WORKFLOW_FROM_PROMPT, value: true }]);
      return;
    }
    if (!isAuthenticated || !projectName) {
      setAssistantFeatures(null);
      return;
    }

    let cancelled = false;
    fetchWorkflowAssistantFeatures({ projectName, getToken: getOttoToken })
      .then((features) => {
        if (!cancelled) setAssistantFeatures(features);
      })
      .catch(() => {
        // Fail closed: disable the assistant feature when the fetch fails.
        if (!cancelled) setAssistantFeatures([]);
      });

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName, isAuthenticated, setAssistantFeatures]);
}

/** Whether the subscription's "create workflow from prompt" capability is enabled. */
export function useCreateWorkflowFromPromptEnabled(): boolean {
  return useAppStore((s) =>
    isFeatureEnabled(s.assistantFeatures ?? undefined, CREATE_WORKFLOW_FROM_PROMPT),
  );
}

